/**
 * Add-on products (CALLOUT)
 * - Merge add-ons into SAME cart request
 * - Reliable UI resync
 * - Tooltip popover
 * - Cart discount UI:
 *   product => use theme old price + discounted + badge
 *   add-ons => original striked + discounted
 */

(function () {
  const SELECTORS = {
    container: '[data-js-addon-container]',
    item: '[data-js-addon-item]',
    checkbox: 'input[type="checkbox"][data-addon-variant-id]',
    infoBtn: '[data-js-addon-info-button]',
    tip: '[data-js-addon-tip]',
    tipClose: '[data-js-addon-tip-close]',
    addonCurrent: '[data-js-addon-current]',
    addonDiscounted: '[data-js-addon-discounted]',
    priceOriginal: '[data-js-price-original]',
    discountPill: '[data-js-discount-pill]',
  };

  const STORAGE_KEY = 'lf_pending_addons_saftkur_callout_v1';
  const CART_CHANGED_EVENT = 'lf:cart-changed';

  // Written by discount-apply.js once it has resolved a discount code. Price rendering is
  // card-scoped, so add-ons are an optional enhancement rather than a prerequisite.
  const RESOLVED_DISCOUNT_KEY = 'scDiscountData';
  const DISCOUNT_RESOLVED_EVENT = 'lf:discount-resolved';
  const CARD_SELECTOR = '.SaftkurCallout__product-card';

  const CART_MUT_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  const CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/i;

  const HEADER_SEARCH_SELECTOR = [
    'sticky-header',
    '.header-wrapper',
    'header',
    'details-modal',
    '.header__search',
    '.search-modal',
    '.modal__toggle',
    '.modal-overlay',
    'predictive-search',
    '[data-predictive-search]',
    '#Search-In-Modal',
    'input[type="search"]',
    '.search__input',
    '.field__input',
    '.LIV-547-searchbar',
    '.LIV-547-searchbar-mobile',
    '#cart-icon-bubble',
    '.header__icon--cart',
  ].join(',');

  let lastCart = null;

  function isInHeaderOrSearch(el) {
    try {
      return !!(el && el.closest && el.closest(HEADER_SEARCH_SELECTOR));
    } catch (e) {
      return false;
    }
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to load cart');
    return res.json();
  }

  function toInt(val, fallback = 0) {
    const n = parseInt(String(val ?? '').replace(/[^\d-]/g, ''), 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function formatMoney(cents, moneyFormat) {
    try {
      if (window.Shopify && typeof window.Shopify.scFormatMoney === 'function') {
        return window.Shopify.scFormatMoney(cents);
      }
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, moneyFormat);
      }
    } catch (e) {}

    const iso =
      (window.Shopify &&
        window.Shopify.cartCurrency &&
        window.Shopify.cartCurrency.iso_code) ||
      'EUR';

    const amount = cents / 100;

    if (iso === 'CHF') return 'CHF ' + amount.toFixed(2);

    return amount.toFixed(2).replace('.', ',') + ' €';
  }

  function getMoneyFormat(container) {
    return container.getAttribute('data-money-format') || '${{amount}}';
  }

  function getParentVariantId(container) {
    return String(container.getAttribute('data-parent-variant-id') || '');
  }

  function getBasePrice(el) {
    const declared = toInt(el.getAttribute('data-base-price'), 0);

    if (declared > 0) return declared;

    // Never let a missing attribute render the price as 0,00 €: the Liquid-rendered
    // .price node always carries the variant price.
    const priceEl = el.querySelector('.price');

    return priceEl
      ? toInt(priceEl.dataset.originalPrice, 0)
      : 0;
  }

  function getDuration(container) {
    const d = toInt(container.getAttribute('data-duration'), 0);

    return d > 0 ? d : 0;
  }

  // Accepts a product card or an add-on container: both carry data-price-target-id.
  function getPriceTarget(el) {
    const targetId = el.getAttribute('data-price-target-id');

    if (targetId) {
      const byId = document.getElementById(targetId);

      if (byId) return byId;
    }

    const card = getProductCard(el);

    return card
      ? card.querySelector('.SaftkurCallout__PriceWrap')
      : null;
  }

  function getProductCard(el) {
    return el.closest('.SaftkurCallout__product-card');
  }

  function setPriceLoadingState(container, isLoading) {
    const card = getProductCard(container);

    if (!card) return;

    card.classList.toggle('is-price-loading', !!isLoading);
  }

  function setCartDiscountState(container, isActive) {
    const card = getProductCard(container);

    if (!card) return;

    card.classList.toggle('is-cart-discount-active', !!isActive);
  }

  function findCurrentProductPriceNode(priceRoot) {
    if (!priceRoot) return null;

    return (
      priceRoot.querySelector(
        '.price__sale .price-item--sale.price-item--last'
      ) ||
      priceRoot.querySelector(
        '.price__sale .price-item--sale'
      ) ||
      priceRoot.querySelector(
        '.price__regular .price-item--regular'
      ) ||
      priceRoot.querySelector(
        '.price-item--sale.price-item--last'
      ) ||
      priceRoot.querySelector(
        '.price-item--sale'
      ) ||
      priceRoot.querySelector(
        '.price-item--regular'
      )
    );
  }

  function findRegularProductPriceNode(priceRoot) {
    if (!priceRoot) return null;

    return (
      priceRoot.querySelector(
        '.price__regular .price-item--regular'
      ) ||
      priceRoot.querySelector(
        '.price .price__regular .price-item'
      ) ||
      priceRoot.querySelector(
        '.price .price-item--regular'
      )
    );
  }

  function updatePricePerDay(priceRoot, centsTotal, duration, moneyFormat) {
    if (!priceRoot || !duration) return;

    const el = priceRoot.querySelector(
      '.SaftkurCallout__price-per-day'
    );

    if (!el) return;

    const perDay = Math.round(centsTotal / duration);

    // The unit is rendered by Liquid: "Tag" for juice cleanses, "Shot" for shot
    // bundles. Falling back to "Tag" keeps older markup without the attribute intact.
    const unitLabel =
      el.getAttribute('data-unit-label') || 'Tag';

    el.textContent =
      `(${formatMoney(perDay, moneyFormat)}/${unitLabel})`;
  }

  function restorePerDayToRegularRow(priceRoot) {
    if (!priceRoot) return;

    const perDayEl = priceRoot.querySelector(
      '.SaftkurCallout__price-per-day'
    );

    const regularWrap = priceRoot.querySelector(
      '.price__regular'
    );

    const regularNode =
      findRegularProductPriceNode(priceRoot);

    if (!perDayEl || !regularWrap) return;

    if (regularWrap.contains(perDayEl)) return;

    if (
      regularNode &&
      regularNode.parentNode === regularWrap
    ) {
      regularNode.insertAdjacentElement(
        'afterend',
        perDayEl
      );

      return;
    }

    regularWrap.appendChild(perDayEl);
  }

  function updateSelectedUI(container) {
    container
      .querySelectorAll(SELECTORS.item)
      .forEach((row) => {
        const cb =
          row.querySelector(SELECTORS.checkbox);

        row.classList.toggle(
          'is-selected',
          !!(cb && cb.checked)
        );
      });
  }

  function normalizeScopeId(raw) {
    const s =
      String(raw ?? '').trim();

    if (!s) return '';

    const parts = s.split('/');

    return (
      parts[parts.length - 1] ||
      s
    ).trim();
  }

  function readResolvedDiscount() {
    try {
      return (
        JSON.parse(
          sessionStorage.getItem(
            RESOLVED_DISCOUNT_KEY
          )
        ) ||
        null
      );
    } catch (e) {
      return null;
    }
  }

  // discount-apply.js resolves the code against the Shopify Admin API and publishes it
  // together with the collections/products it targets. Honour that scope: a code limited
  // to one collection must not discount every card on the page.
  function resolvedDiscountAppliesTo(
    card,
    resolved
  ) {
    if (!card || !resolved) return false;

    const priceEl =
      card.querySelector('.price');

    if (!priceEl) return false;

    const productId =
      normalizeScopeId(
        priceEl.dataset.productId
      );

    if (
      productId &&
      (resolved.products || []).includes(
        productId
      )
    ) {
      return true;
    }

    const scoped =
      new Set(
        resolved.collections || []
      );

    return String(
      priceEl.dataset.collections || ''
    )
      .split(',')
      .map(normalizeScopeId)
      .filter(Boolean)
      .some((id) =>
        scoped.has(id)
      );
  }

  // Used when the cart cannot report the discount yet: an empty cart never returns
  // discount_codes, so a customer arriving on /discount/<code> depends on this path.
  function getSessionDiscount(card) {
    const resolved =
      readResolvedDiscount();

    if (
      !resolvedDiscountAppliesTo(
        card,
        resolved
      )
    ) {
      return null;
    }

    const type =
      String(
        resolved.discount?.type || ''
      );

    const value =
      parseFloat(
        resolved.discount?.value
      );

    if (
      !Number.isFinite(value) ||
      value <= 0
    ) {
      return null;
    }

    if (type === 'percentage') {
      return {
        active: true,
        factor:
          1 -
          value / 100,
        pct:
          window.roundDiscount(
            value
          ),
      };
    }

    if (type === 'fixed') {
      const basePrice =
        getBasePrice(card);

      if (basePrice <= 0) {
        return null;
      }

      const discounted =
        Math.max(
          0,
          basePrice -
            Math.round(
              value * 100
            )
        );

      const factor =
        discounted /
        basePrice;

      return {
        active: true,
        factor,
        pct:
          window.roundDiscount(
            (
              1 -
              factor
            ) * 100
          ),
      };
    }

    return null;
  }

  function findCartLine(
    cart,
    variantId
  ) {
    const id =
      String(variantId || '');

    if (!id) return null;

    return (
      (cart?.items || []).find(
        (item) =>
          String(
            item.variant_id
          ) === id
      ) ||
      null
    );
  }

  function findCartLineForCard(
    card,
    cart
  ) {
    const line =
      findCartLine(
        cart,
        card?.getAttribute(
          'data-variant-id'
        )
      );

    // An add-on line can never stand in for its parent product's price.
    if (
      line &&
      String(
        line.properties?._addon || ''
      ) === 'true'
    ) {
      return null;
    }

    return line;
  }

  // A line's own allocations are the only trustworthy statement that the cart's code
  // reaches this product. Cart-wide totals say nothing about an individual card: a code
  // that discounts some other product still drags total below original_total_price.
  function discountFromCartLine(line) {
    for (
      const alloc of
      line.line_level_discount_allocations ||
      []
    ) {
      const app =
        alloc.discount_application;

      if (
        app &&
        app.value_type ===
          'percentage' &&
        parseFloat(
          app.value
        ) > 0
      ) {
        const codePct =
          parseFloat(
            app.value
          );

        return {
          active: true,
          factor:
            1 -
            codePct / 100,
          pct:
            window.roundDiscount(
              codePct
            ),
        };
      }
    }

    // Fixed-amount and other discount types: derive the factor from this line alone, so it
    // is not diluted by the rest of the cart (deposit, add-ons, other products).
    const original =
      toInt(
        line.original_line_price,
        0
      );

    const final =
      toInt(
        line.final_line_price,
        -1
      );

    if (
      original > 0 &&
      final >= 0 &&
      final < original
    ) {
      const factor =
        final /
        original;

      return {
        active: true,
        factor,
        pct:
          window.roundDiscount(
            (
              1 -
              factor
            ) * 100
          ),
      };
    }

    return null;
  }

  const NO_DISCOUNT = {
    active: false,
    factor: 1,
    pct: 0,
  };

  function getCartDiscount(
    card,
    cart
  ) {
    const line =
      findCartLineForCard(
        card,
        cart
      );

    // The cart is authoritative for a product it actually contains: if this line carries
    // no discount, the code does not reach this product, however large the cart discount.
    if (line) {
      return (
        discountFromCartLine(
          line
        ) ||
        NO_DISCOUNT
      );
    }

    // Not in the cart, so the cart says nothing about it. Fall back to the discount
    // discount-apply.js resolved from the URL/cookie, which carries its own scope.
    return (
      getSessionDiscount(
        card
      ) ||
      NO_DISCOUNT
    );
  }

  function movePerDayIntoSaleRow(
    priceRoot,
    saleWrap,
    saleCurrentNode,
    pillEl
  ) {
    if (
      !priceRoot ||
      !saleWrap
    ) {
      return;
    }

    const perDayEl =
      priceRoot.querySelector(
        '.SaftkurCallout__price-per-day'
      );

    if (!perDayEl) return;

    if (
      saleWrap.contains(
        perDayEl
      )
    ) {
      return;
    }

    if (
      pillEl &&
      pillEl.parentNode ===
        saleWrap
    ) {
      saleWrap.insertBefore(
        perDayEl,
        pillEl
      );

      return;
    }

    if (
      saleCurrentNode &&
      saleCurrentNode.parentNode ===
        saleWrap
    ) {
      saleCurrentNode.insertAdjacentElement(
        'afterend',
        perDayEl
      );

      return;
    }

    saleWrap.appendChild(
      perDayEl
    );
  }

  function applyDiscountToProduct(
    card,
    cart
  ) {
    const discount =
      getCartDiscount(
        card,
        cart
      );

    const priceRoot =
      getPriceTarget(card);

    if (!priceRoot) return;

    const row =
      priceRoot.closest(
        '.SaftkurCallout__PriceRow'
      );

    const originalEl =
      row
        ? row.querySelector(
            SELECTORS.priceOriginal
          )
        : null;

    const pillEl =
      row
        ? row.querySelector(
            SELECTORS.discountPill
          )
        : null;

    const basePrice =
      getBasePrice(card);

    const duration =
      getDuration(card);

    const moneyFormat =
      getMoneyFormat(card);

    const saleWrap =
      priceRoot.querySelector(
        '.price__sale'
      );

    const saleOldNode =
      priceRoot.querySelector(
        '.price__sale .price-item--regular'
      ) ||
      priceRoot.querySelector(
        '.price .price-item.price-item--regular'
      );

    const saleCurrentNode =
      priceRoot.querySelector(
        '.price__sale .price-item--sale.price-item--last'
      ) ||
      priceRoot.querySelector(
        '.price__sale .price-item--sale'
      ) ||
      priceRoot.querySelector(
        '.price-item--sale.price-item--last'
      ) ||
      priceRoot.querySelector(
        '.price-item--sale'
      ) ||
      priceRoot.querySelector(
        '.price__regular .price-item--regular'
      ) ||
      priceRoot.querySelector(
        '.price-item--regular'
      );

    const regularNode =
      findRegularProductPriceNode(
        priceRoot
      );

    if (!discount.active) {
      // No cart/code discount, but the product itself may be on sale
      // (compare_at_price > price). The theme already renders the struck
      // price via Liquid, so just surface the savings pill here.
      const priceEl =
        priceRoot.querySelector(
          '.price'
        );

      const compareAtCents =
        priceEl
          ? toInt(
              priceEl.dataset
                .compareAtPrice,
              0
            )
          : 0;

      const priceCents =
        priceEl
          ? toInt(
              priceEl.dataset
                .originalPrice,
              0
            )
          : 0;

      const hasCompareSale =
        compareAtCents > 0 &&
        priceCents > 0 &&
        compareAtCents >
          priceCents;

      if (
        hasCompareSale &&
        pillEl
      ) {
        const comparePct =
          window.roundDiscount(
            (
              (
                compareAtCents -
                priceCents
              ) /
              compareAtCents
            ) * 100
          );

        setCartDiscountState(
          card,
          false
        );

        if (originalEl) {
          originalEl.hidden =
            true;

          originalEl.textContent =
            '';
        }

        pillEl.hidden =
          false;

        pillEl.textContent =
          `-${comparePct}%`;

        if (
          saleWrap &&
          pillEl.parentNode !==
            saleWrap
        ) {
          saleWrap.appendChild(
            pillEl
          );
        }

        if (duration) {
          updatePricePerDay(
            priceRoot,
            priceCents,
            duration,
            moneyFormat
          );

          movePerDayIntoSaleRow(
            priceRoot,
            saleWrap,
            saleCurrentNode,
            pillEl
          );
        }

        return;
      }

      setCartDiscountState(
        card,
        false
      );

      if (originalEl) {
        originalEl.hidden =
          true;

        originalEl.textContent =
          '';
      }

      if (pillEl) {
        pillEl.hidden =
          true;

        pillEl.textContent =
          '';
      }

      if (regularNode) {
        regularNode.textContent =
          formatMoney(
            basePrice,
            moneyFormat
          );
      }

      if (duration) {
        updatePricePerDay(
          priceRoot,
          basePrice,
          duration,
          moneyFormat
        );

        restorePerDayToRegularRow(
          priceRoot
        );
      }

      return;
    }

    setCartDiscountState(
      card,
      true
    );

    const discounted =
      Math.round(
        basePrice *
          discount.factor
      );

    // The code discounts the selling price, but the customer's saving is measured against
    // the compare-at price (RRP) when the product is already on sale. Strike the RRP and
    // show the combined percentage, so this row agrees with the cart and the checkout.
    const priceEl =
      priceRoot.querySelector(
        '.price'
      );

    const compareAtCents =
      priceEl
        ? toInt(
            priceEl.dataset
              .compareAtPrice,
            0
          )
        : 0;

    const strikeCents =
      compareAtCents >
      basePrice
        ? compareAtCents
        : basePrice;

    const exactDiscounted =
      basePrice *
      discount.factor;

    const totalPct =
      strikeCents > 0
        ? window.roundDiscount(
            (
              (
                strikeCents -
                exactDiscounted
              ) /
              strikeCents
            ) * 100
          )
        : discount.pct;

    if (originalEl) {
      originalEl.hidden =
        true;

      originalEl.textContent =
        '';
    }

    if (saleOldNode) {
      saleOldNode.textContent =
        formatMoney(
          strikeCents,
          moneyFormat
        );
    }

    if (saleCurrentNode) {
      saleCurrentNode.textContent =
        formatMoney(
          discounted,
          moneyFormat
        );
    }

    if (pillEl) {
      pillEl.hidden =
        false;

      pillEl.textContent =
        `-${Math.max(
          0,
          totalPct
        )}%`;

      if (
        saleWrap &&
        pillEl.parentNode !==
          saleWrap
      ) {
        saleWrap.appendChild(
          pillEl
        );
      }
    }

    if (duration) {
      updatePricePerDay(
        priceRoot,
        discounted,
        duration,
        moneyFormat
      );
    }

    movePerDayIntoSaleRow(
      priceRoot,
      saleWrap,
      saleCurrentNode,
      pillEl
    );
  }

  function applyDiscountToAddons(
    container,
    cart
  ) {
    const parentDiscount =
      getCartDiscount(
        getProductCard(
          container
        ),
        cart
      );

    const moneyFormat =
      getMoneyFormat(
        container
      );

    container
      .querySelectorAll(
        SELECTORS.item
      )
      .forEach((row) => {
        const cb =
          row.querySelector(
            SELECTORS.checkbox
          );

        const currentEl =
          row.querySelector(
            SELECTORS.addonCurrent
          );

        const discountedEl =
          row.querySelector(
            SELECTORS.addonDiscounted
          );

        if (
          !cb ||
          !currentEl ||
          !discountedEl
        ) {
          return;
        }

        const addonCents =
          toInt(
            cb.getAttribute(
              'data-addon-price'
            ),
            0
          );

        // An add-on already in the cart states its own discount. Otherwise preview with the
        // parent's, the best estimate available before the add-on is added.
        const addonLine =
          findCartLine(
            cart,
            cb.getAttribute(
              'data-addon-variant-id'
            )
          );

        const discount =
          addonLine
            ? (
                discountFromCartLine(
                  addonLine
                ) ||
                NO_DISCOUNT
              )
            : parentDiscount;

        if (
          !discount.active ||
          !addonCents
        ) {
          row.classList.remove(
            'is-cart-discount-active'
          );

          discountedEl.hidden =
            true;

          discountedEl.textContent =
            '';

          return;
        }

        const discounted =
          Math.round(
            addonCents *
              discount.factor
          );

        row.classList.add(
          'is-cart-discount-active'
        );

        discountedEl.hidden =
          false;

        discountedEl.textContent =
          formatMoney(
            discounted,
            moneyFormat
          );
      });
  }

  function buildAddonOwnershipIndex(
    cart
  ) {
    const owned =
      new Set();

    (
      cart?.items ||
      []
    ).forEach((it) => {
      const vid =
        String(
          it.variant_id ||
            ''
        );

      const props =
        it.properties ||
        {};

      const isAddon =
        String(
          props?._addon ||
            ''
        ) === 'true';

      const ownerParent =
        String(
          props?._addon_for_variant ||
            ''
        );

      if (
        isAddon &&
        vid &&
        ownerParent
      ) {
        owned.add(
          `${vid}::${ownerParent}`
        );
      }
    });

    return owned;
  }

  function markContainerDirty(
    container
  ) {
    if (!container) return;

    container.dataset
      .addonSelectionDirty =
      'true';
  }

  function clearContainerDirty(
    container
  ) {
    if (!container) return;

    delete container.dataset
      .addonSelectionDirty;
  }

  function isContainerDirty(
    container
  ) {
    return !!(
      container &&
      container.dataset
        .addonSelectionDirty ===
        'true'
    );
  }

  function clearPendingContainerDirty(
    pending
  ) {
    if (!pending) return;

    const productFormId =
      String(
        pending.productFormId ||
          ''
      );

    const parentVariantId =
      String(
        pending.parentVariantId ||
          ''
      );

    const container =
      getAddonContainers().find(
        (candidate) => {
          const candidateFormId =
            String(
              candidate.getAttribute(
                'data-product-form-id'
              ) ||
                ''
            );

          const candidateParentVariantId =
            String(
              candidate.getAttribute(
                'data-parent-variant-id'
              ) ||
                ''
            );

          if (
            productFormId &&
            candidateFormId ===
              productFormId
          ) {
            return true;
          }

          return !!(
            parentVariantId &&
            candidateParentVariantId ===
              parentVariantId
          );
        }
      );

    if (container) {
      clearContainerDirty(
        container
      );
    }
  }

  // Add-on state only. Price rendering is card-scoped and lives in applyDiscountToProduct,
  // so a card whose add-on blocks are disabled still tracks the cart.
  //
  // IMPORTANT:
  // While the customer is choosing add-ons, cart resyncs must NOT overwrite the
  // local checkbox state. The cart becomes authoritative again only after the
  // pending add-to-cart request has completed successfully.
  function applyCartStateToContainer(
    container,
    cart,
    options = {}
  ) {
    const {
      forceSelectionSync = false,
    } = options;

    const ownerSet =
      buildAddonOwnershipIndex(
        cart
      );

    const parentVariantId =
      getParentVariantId(
        container
      );

    const canSyncSelection =
      forceSelectionSync ||
      !isContainerDirty(
        container
      );

    if (canSyncSelection) {
      container
        .querySelectorAll(
          SELECTORS.checkbox
        )
        .forEach((cb) => {
          const addonVid =
            String(
              toInt(
                cb.getAttribute(
                  'data-addon-variant-id'
                ),
                0
              ) ||
                ''
            );

          if (!addonVid) return;

          const shouldBeChecked =
            ownerSet.size > 0
              ? ownerSet.has(
                  `${addonVid}::${parentVariantId}`
                )
              : false;

          cb.checked =
            !!shouldBeChecked;
        });

      /*
       * IMPORTANT:
       * A forced sync means the cart drawer has already been re-rendered,
       * therefore /cart.js is now authoritative.
       *
       * Remove the local dirty flag so a removed Gluco/Gemüse can also
       * remove the selected button state.
       */
      if (forceSelectionSync) {
        clearContainerDirty(
          container
        );
      }
    }

    updateSelectedUI(
      container
    );

    applyDiscountToAddons(
      container,
      cart
    );
  }

  function getPriceCards() {
    return Array.from(
      document.querySelectorAll(
        CARD_SELECTOR
      )
    ).filter(
      (card) =>
        card.querySelector(
          '.SaftkurCallout__PriceRow'
        )
    );
  }

  function getAddonContainers() {
    /*
     * This asset belongs to the ORIGINAL Saftkur callout.
     * Do not initialize other add-on components (for example LF-09 variation
     * FeatCollectionImage) that reuse the generic data-js-addon-container hook.
     */
    return Array.from(
      document.querySelectorAll(
        `${CARD_SELECTOR} ${SELECTORS.container}`
      )
    );
  }

  function applyCartStateToUI(cart) {
    lastCart = cart;

    getPriceCards().forEach(
      (card) =>
        applyDiscountToProduct(
          card,
          cart
        )
    );

    getAddonContainers().forEach(
      (container) =>
        applyCartStateToContainer(
          container,
          cart,
          {
            forceSelectionSync:
              false,
          }
        )
    );
  }

  /*
   * Normal cart sync:
   * preserve a local selection while the user is still choosing an add-on.
   *
   * Forced cart sync:
   * used after rerenderCart, when the cart drawer has already been refreshed.
   * In that case the cart is authoritative and removed add-ons must be
   * deselected from the Saftkur card as well.
   */
  async function syncFromCart(
    forceSelectionSync = false
  ) {
    const cards =
      getPriceCards();

    cards.forEach(
      (card) =>
        setPriceLoadingState(
          card,
          true
        )
    );

    try {
      const cart =
        await fetchCart();

      lastCart =
        cart;

      getPriceCards().forEach(
        (card) =>
          applyDiscountToProduct(
            card,
            cart
          )
      );

      getAddonContainers().forEach(
        (container) =>
          applyCartStateToContainer(
            container,
            cart,
            {
              forceSelectionSync,
            }
          )
      );
    } catch (e) {
      console.error(
        '[LF ADDON CALLOUT] cart sync failed',
        e
      );
    } finally {
      cards.forEach(
        (card) =>
          setPriceLoadingState(
            card,
            false
          )
      );
    }
  }

  function createThrottled(
    fn,
    wait
  ) {
    let t = null;

    return function () {
      if (t) return;

      t = setTimeout(
        () => {
          t = null;
          fn();
        },
        wait
      );
    };
  }

  function bindCartResync() {
    const normalResync =
      createThrottled(
        () =>
          syncFromCart(false),
        200
      );

    const forceResync =
      createThrottled(
        () =>
          syncFromCart(true),
        200
      );

    /*
     * Generic cart events:
     * preserve any add-on selection that the customer has made locally
     * but has not submitted yet.
     */
    document.addEventListener(
      CART_CHANGED_EVENT,
      normalResync
    );

    document.addEventListener(
      'cart:updated',
      normalResync
    );

    document.addEventListener(
      'cart:refresh',
      normalResync
    );

    /*
     * The theme dispatches rerenderCart after the cart drawer is refreshed.
     *
     * At this point /cart.js is authoritative:
     *
     * - if Gluco is still in cart -> keep selected
     * - if Gluco was removed -> uncheck it + remove is-selected
     * - same for Gemüse
     */
    document.addEventListener(
      'rerenderCart',
      forceResync
    );

    document.addEventListener(
      DISCOUNT_RESOLVED_EVENT,
      (event) => {
        const cart =
          event.detail
            ? lastCart
            : null;

        getPriceCards().forEach(
          (card) =>
            applyDiscountToProduct(
              card,
              cart
            )
        );

        getAddonContainers().forEach(
          (container) =>
            applyDiscountToAddons(
              container,
              cart
            )
        );
      }
    );

    document.addEventListener(
      'visibilitychange',
      () => {
        if (
          document.visibilityState ===
          'visible'
        ) {
          normalResync();
        }
      }
    );

    window.addEventListener(
      'focus',
      normalResync
    );
  }

  function resetTipInlinePosition(
    tip
  ) {
    if (!tip) return;

    tip.style.position =
      '';

    tip.style.top =
      '';

    tip.style.left =
      '';

    tip.style.removeProperty(
      '--arrow-x'
    );

    tip.style.visibility =
      '';
  }

  function closeAllTips(
    container
  ) {
    container
      .querySelectorAll(
        SELECTORS.tip
      )
      .forEach((t) => {
        t.classList.remove(
          'is-open',
          'is-above'
        );

        t.setAttribute(
          'aria-hidden',
          'true'
        );

        if (
          t.hasAttribute(
            'inert'
          )
        ) {
          t.removeAttribute(
            'inert'
          );
        }

        resetTipInlinePosition(
          t
        );
      });
  }

  function closeAllTipsEverywhere() {
    document
      .querySelectorAll(
        SELECTORS.tip
      )
      .forEach((t) => {
        t.classList.remove(
          'is-open',
          'is-above'
        );

        t.setAttribute(
          'aria-hidden',
          'true'
        );

        if (
          t.hasAttribute(
            'inert'
          )
        ) {
          t.removeAttribute(
            'inert'
          );
        }

        resetTipInlinePosition(
          t
        );
      });
  }

  function ensureTipInteractive(
    tip
  ) {
    if (!tip) return;

    tip.setAttribute(
      'aria-hidden',
      'false'
    );

    if (
      tip.hasAttribute(
        'inert'
      )
    ) {
      tip.removeAttribute(
        'inert'
      );
    }
  }

  function positionTipFixed(
    buttonEl,
    tipEl
  ) {
    if (
      !buttonEl ||
      !tipEl
    ) {
      return;
    }

    const btnRect =
      buttonEl.getBoundingClientRect();

    const prevVisibility =
      tipEl.style.visibility;

    tipEl.style.visibility =
      'hidden';

    tipEl.style.position =
      'fixed';

    tipEl.classList.add(
      'is-open'
    );

    ensureTipInteractive(
      tipEl
    );

    const tipRect =
      tipEl.getBoundingClientRect();

    const vw =
      window.innerWidth;

    const vh =
      window.innerHeight;

    let top =
      btnRect.bottom +
      10;

    let left =
      btnRect.left +
      btnRect.width / 2 -
      tipRect.width / 2;

    left =
      Math.max(
        12,
        Math.min(
          left,
          vw -
            tipRect.width -
            12
        )
      );

    if (
      top +
        tipRect.height +
        12 >
      vh
    ) {
      top =
        btnRect.top -
        tipRect.height -
        10;

      tipEl.classList.add(
        'is-above'
      );
    } else {
      tipEl.classList.remove(
        'is-above'
      );
    }

    top =
      Math.max(
        8,
        top
      );

    tipEl.style.top =
      `${top}px`;

    tipEl.style.left =
      `${left}px`;

    const arrowCenter =
      btnRect.left +
      btnRect.width / 2 -
      left;

    tipEl.style.setProperty(
      '--arrow-x',
      `${arrowCenter}px`
    );

    tipEl.style.visibility =
      prevVisibility ||
      '';
  }

  function openTip(
    buttonEl,
    tipEl
  ) {
    if (
      !buttonEl ||
      !tipEl
    ) {
      return;
    }

    closeAllTipsEverywhere();

    tipEl.classList.add(
      'is-open'
    );

    ensureTipInteractive(
      tipEl
    );

    positionTipFixed(
      buttonEl,
      tipEl
    );

    setTimeout(
      () => {
        if (
          !tipEl.classList.contains(
            'is-open'
          )
        ) {
          return;
        }

        ensureTipInteractive(
          tipEl
        );

        positionTipFixed(
          buttonEl,
          tipEl
        );
      },
      0
    );

    setTimeout(
      () => {
        if (
          !tipEl.classList.contains(
            'is-open'
          )
        ) {
          return;
        }

        ensureTipInteractive(
          tipEl
        );

        positionTipFixed(
          buttonEl,
          tipEl
        );
      },
      50
    );
  }

  function toggleTip(
    container,
    infoBtn
  ) {
    const id =
      infoBtn.getAttribute(
        'data-addon-info-target'
      );

    if (!id) return;

    const tip =
      container.querySelector(
        `[data-js-addon-tip="${CSS.escape(
          id
        )}"]`
      );

    if (!tip) return;

    const wasOpen =
      tip.classList.contains(
        'is-open'
      );

    closeAllTipsEverywhere();

    if (!wasOpen) {
      openTip(
        infoBtn,
        tip
      );
    }
  }

  function bindTipHandlers(
    container
  ) {
    container.addEventListener(
      'pointerdown',
      (e) => {
        const infoBtn =
          e.target.closest?.(
            SELECTORS.infoBtn
          );

        if (
          !infoBtn ||
          !container.contains(
            infoBtn
          )
        ) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        if (
          typeof e.stopImmediatePropagation ===
          'function'
        ) {
          e.stopImmediatePropagation();
        }
      },
      true
    );

    container.addEventListener(
      'click',
      (e) => {
        const infoBtn =
          e.target.closest?.(
            SELECTORS.infoBtn
          );

        if (
          infoBtn &&
          container.contains(
            infoBtn
          )
        ) {
          e.preventDefault();
          e.stopPropagation();

          if (
            typeof e.stopImmediatePropagation ===
            'function'
          ) {
            e.stopImmediatePropagation();
          }

          toggleTip(
            container,
            infoBtn
          );

          return;
        }

        const tipClose =
          e.target.closest?.(
            SELECTORS.tipClose
          );

        if (
          tipClose &&
          container.contains(
            tipClose
          )
        ) {
          e.preventDefault();
          e.stopPropagation();

          closeAllTips(
            container
          );

          return;
        }
      }
    );
  }

  function ensureGlobalOutsideHandlers() {
    if (
      window.__lfAddonsCalloutGlobalOutsideBound_V9
    ) {
      return;
    }

    window.__lfAddonsCalloutGlobalOutsideBound_V9 =
      true;

    document.addEventListener(
      'click',
      (e) => {
        if (
          isInHeaderOrSearch(
            e.target
          )
        ) {
          return;
        }

        const insideAddon =
          e.target &&
          e.target.closest
            ? e.target.closest(
                SELECTORS.container
              )
            : null;

        if (insideAddon) return;

        closeAllTipsEverywhere();
      }
    );

    window.addEventListener(
      'resize',
      () =>
        closeAllTipsEverywhere()
    );

    window.addEventListener(
      'scroll',
      () =>
        closeAllTipsEverywhere(),
      {
        passive: true,
      }
    );

    document.addEventListener(
      'keydown',
      (e) => {
        if (
          e.key ===
          'Escape'
        ) {
          closeAllTipsEverywhere();
        }
      }
    );
  }

  function writePending(payload) {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(
          payload
        )
      );
    } catch (e) {}
  }

  function readPending() {
    try {
      const raw =
        sessionStorage.getItem(
          STORAGE_KEY
        );

      if (!raw) return null;

      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function clearPending() {
    try {
      sessionStorage.removeItem(
        STORAGE_KEY
      );
    } catch (e) {}
  }

  function getSelectedAddonVariantIds(
    container
  ) {
    const ids = [];

    container
      .querySelectorAll(
        SELECTORS.checkbox
      )
      .forEach((cb) => {
        if (!cb.checked) return;

        const vid =
          toInt(
            cb.getAttribute(
              'data-addon-variant-id'
            ),
            0
          );

        if (vid) {
          ids.push(vid);
        }
      });

    return ids;
  }

  function bindCheckboxUI(
    container
  ) {
    container.addEventListener(
      'change',
      (e) => {
        const cb =
          e.target &&
          e.target.matches &&
          e.target.matches(
            SELECTORS.checkbox
          )
            ? e.target
            : null;

        if (!cb) return;

        // The user has intentionally changed this selection. Keep that local
        // state stable until the ATC request has actually completed.
        markContainerDirty(
          container
        );

        updateSelectedUI(
          container
        );

        if (lastCart) {
          applyDiscountToAddons(
            container,
            lastCart
          );
        }
      }
    );
  }

  function getAddonContainerForForm(
    form
  ) {
    if (
      !form ||
      !form.id
    ) {
      return null;
    }

    const formId =
      form.id;

    const card =
      form.closest(
        CARD_SELECTOR
      );

    /*
     * Prefer the container inside the SAME product card. This is critical for
     * the 3/5/7-day callout because every card owns its own form and its own
     * selected add-ons.
     */
    if (card) {
      const localContainers =
        Array.from(
          card.querySelectorAll(
            SELECTORS.container
          )
        );

      const localMatch =
        localContainers.find(
          (container) =>
            container.getAttribute(
              'data-product-form-id'
            ) === formId
        );

      if (localMatch) {
        return localMatch;
      }
    }

    /* Fallback for valid callout markup outside the immediate card scope. */
    return (
      getAddonContainers().find(
        (container) =>
          container.getAttribute(
            'data-product-form-id'
          ) === formId
      ) ||
      null
    );
  }

  function bindGlobalFormCapture() {
    if (
      window.__lfAddonsGlobalFormCaptureBoundCalloutV11
    ) {
      return;
    }

    window.__lfAddonsGlobalFormCaptureBoundCalloutV11 =
      true;

    document.addEventListener(
      'submit',
      (event) => {
        const form =
          event.target &&
          event.target.matches &&
          event.target.matches(
            'form'
          )
            ? event.target
            : event.target &&
                event.target.closest
              ? event.target.closest(
                  'form'
                )
              : null;

        if (!form) return;

        const container =
          getAddonContainerForForm(
            form
          );

        if (!container) return;

        const addonIds =
          getSelectedAddonVariantIds(
            container
          );

        if (!addonIds.length) {
          clearPending();
          return;
        }

        writePending({
          createdAt:
            Date.now(),

          addonVariantIds:
            addonIds,

          parentHandle:
            container.getAttribute(
              'data-parent-handle'
            ) ||
            '',

          parentVariantId:
            container.getAttribute(
              'data-parent-variant-id'
            ) ||
            '',

          productFormId:
            form.id ||
            '',
        });

        console.warn(
          '[LF ADDON CALLOUT] captured add-ons for submit',
          {
            formId:
              form.id ||
              '',

            parentVariantId:
              container.getAttribute(
                'data-parent-variant-id'
              ) ||
              '',

            addonVariantIds:
              addonIds,
          }
        );
      },
      true
    );
  }

  function dispatchCartChanged() {
    try {
      document.dispatchEvent(
        new CustomEvent(
          CART_CHANGED_EVENT
        )
      );
    } catch (e) {}
  }

  function buildItemsFromFormData(
    fd
  ) {
    const mainId =
      toInt(
        fd.get('id'),
        0
      );

    const qty =
      toInt(
        fd.get(
          'quantity'
        ),
        1
      ) ||
      1;

    const properties =
      {};

    for (
      const [k, v] of
      fd.entries()
    ) {
      const m =
        String(k).match(
          /^properties\[(.+)\]$/
        );

      if (m) {
        properties[
          m[1]
        ] = v;
      }
    }

    const item = {
      id: mainId,
      quantity: qty,
    };

    if (
      Object.keys(
        properties
      ).length
    ) {
      item.properties =
        properties;
    }

    return mainId
      ? [item]
      : [];
  }

  function buildItemsFromUrlEncoded(
    bodyStr
  ) {
    const usp =
      new URLSearchParams(
        bodyStr
      );

    const fd =
      new FormData();

    for (
      const [k, v] of
      usp.entries()
    ) {
      fd.append(
        k,
        v
      );
    }

    return buildItemsFromFormData(
      fd
    );
  }

  function tryParseJSONBody(
    body
  ) {
    try {
      return JSON.parse(
        body
      );
    } catch (e) {
      return null;
    }
  }

  function ensureNetworkPatches() {
    if (
      window.__lfAddonsNetworkPatchedCalloutV8
    ) {
      return;
    }

    window.__lfAddonsNetworkPatchedCalloutV8 =
      true;

    const originalFetch =
      window.fetch.bind(
        window
      );

    window.fetch =
      async function (
        input,
        init
      ) {
        const url =
          typeof input ===
          'string'
            ? input
            : input &&
                input.url
              ? input.url
              : '';

        const isCartMutation =
          CART_MUT_RE.test(
            url
          );

        const isCartAdd =
          CART_ADD_RE.test(
            url
          );

        if (
          isCartAdd &&
          !window.__lfAddonsMergingNowCallout &&
          init &&
          (
            init.method ||
            'POST'
          ).toUpperCase() ===
            'POST'
        ) {
          const pending =
            readPending();

          const isFresh =
            pending &&
            Date.now() -
              (
                pending.createdAt ||
                0
              ) <=
              60 * 1000;

          const addonIds =
            isFresh &&
            Array.isArray(
              pending.addonVariantIds
            )
              ? pending.addonVariantIds
              : [];

          if (
            addonIds.length
          ) {
            try {
              window.__lfAddonsMergingNowCallout =
                true;

              let mainItems =
                [];

              const headers =
                new Headers(
                  init.headers ||
                    {}
                );

              const body =
                init.body;

              if (
                body instanceof
                FormData
              ) {
                mainItems =
                  buildItemsFromFormData(
                    body
                  );
              } else if (
                typeof body ===
                'string'
              ) {
                const json =
                  tryParseJSONBody(
                    body
                  );

                if (
                  json &&
                  Array.isArray(
                    json.items
                  )
                ) {
                  mainItems =
                    json.items;
                } else {
                  mainItems =
                    buildItemsFromUrlEncoded(
                      body
                    );
                }
              } else if (
                body &&
                typeof body ===
                  'object'
              ) {
                if (
                  Array.isArray(
                    body.items
                  )
                ) {
                  mainItems =
                    body.items;
                }
              }

              const items =
                Array.isArray(
                  mainItems
                )
                  ? [
                      ...mainItems,
                    ]
                  : [];

              const parentHandle =
                pending.parentHandle ||
                '';

              const parentVariantId =
                pending.parentVariantId ||
                '';

              addonIds.forEach(
                (vid) => {
                  items.push({
                    id: vid,
                    quantity: 1,

                    properties: {
                      _addon:
                        'true',

                      _addon_for:
                        parentHandle,

                      _addon_for_variant:
                        parentVariantId,
                    },
                  });
                }
              );

              clearPending();

              headers.set(
                'Content-Type',
                'application/json'
              );

              const newInit = {
                ...init,
                headers,

                body:
                  JSON.stringify({
                    items,
                  }),
              };

              const res =
                await originalFetch(
                  input,
                  newInit
                );

              if (
                res &&
                res.ok
              ) {
                // The cart now contains the submitted add-ons, so the cart can
                // become authoritative again for this specific product card.
                clearPendingContainerDirty(
                  pending
                );

                dispatchCartChanged();
              }

              return res;
            } catch (e) {
              clearPending();

              window.__lfAddonsMergingNowCallout =
                false;

              return originalFetch(
                input,
                init
              );
            } finally {
              window.__lfAddonsMergingNowCallout =
                false;
            }
          }
        }

        const res =
          await originalFetch(
            input,
            init
          );

        if (
          isCartMutation &&
          res &&
          res.ok
        ) {
          dispatchCartChanged();
        }

        return res;
      };

    const OriginalXHR =
      window.XMLHttpRequest;

    function PatchedXHR() {
      const xhr =
        new OriginalXHR();

      let _url =
        '';

      const originalOpen =
        xhr.open;

      xhr.open =
        function (
          method,
          url
        ) {
          _url =
            String(
              url ||
                ''
            );

          return originalOpen.apply(
            xhr,
            arguments
          );
        };

      xhr.addEventListener(
        'load',
        function () {
          try {
            if (
              xhr.status >=
                200 &&
              xhr.status <
                300 &&
              CART_MUT_RE.test(
                _url
              )
            ) {
              dispatchCartChanged();
            }
          } catch (e) {}
        }
      );

      return xhr;
    }

    window.XMLHttpRequest =
      PatchedXHR;
  }

  // Add-ons are optional. Everything here is add-on chrome; the price row is driven
  // separately by applyDiscountToProduct so a card with no add-on blocks still works.
  function initContainer(
    container
  ) {
    if (
      !container ||
      container.dataset
        .lfAddonCalloutInitialized ===
        'true'
    ) {
      return;
    }

    container.dataset
      .lfAddonCalloutInitialized =
      'true';

    bindTipHandlers(
      container
    );

    bindCheckboxUI(
      container
    );

    updateSelectedUI(
      container
    );
  }

  function boot() {
    const cards =
      getPriceCards();

    const containers =
      getAddonContainers();

    if (
      !cards.length &&
      !containers.length
    ) {
      return;
    }

    // Needed by cards too: the fetch/XHR patch is what emits CART_CHANGED_EVENT.
    ensureNetworkPatches();

    // Capture the submit dynamically so 3/5/7-day cards always resolve their own
    // add-on container, even if forms/components are re-rendered after initial boot.
    bindGlobalFormCapture();

    // Tooltip chrome only exists alongside add-ons; keep its global listeners off pages
    // that render no add-on container.
    if (
      containers.length
    ) {
      ensureGlobalOutsideHandlers();

      containers.forEach(
        initContainer
      );
    }

    bindCartResync();

    syncFromCart();
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot
    );
  } else {
    boot();
  }

  // Tells discount-apply.js that this script owns .SaftkurCallout__PriceRow prices. Set
  // last: if anything above threw, discount-apply.js keeps handling those prices itself.
  window.__lfSaftkurCalloutPriceOwner =
    true;
})();


(function patchBestsellerInfoJump() {
  if (
    window.__lf_patchBestsellerInfoJumpBound_V5
  ) {
    return;
  }

  window.__lf_patchBestsellerInfoJumpBound_V5 =
    true;

  function isBestsellerInfoButton(
    btn
  ) {
    return !!(
      btn &&
      btn.closest &&
      btn.closest(
        '.SaftkurCallout__product-card--bestseller'
      )
    );
  }

  function getTipFromButton(
    btn
  ) {
    const tipId =
      btn.getAttribute(
        'data-addon-info-target'
      );

    if (!tipId) {
      return null;
    }

    const container =
      btn.closest(
        '[data-js-addon-container]'
      );

    const selector =
      `[data-js-addon-tip="${CSS.escape(
        tipId
      )}"]`;

    return (
      (
        container &&
        container.querySelector(
          selector
        )
      ) ||
      document.querySelector(
        selector
      )
    );
  }

  function resetTipInlinePosition(
    tip
  ) {
    if (!tip) {
      return;
    }

    tip.style.position =
      '';

    tip.style.top =
      '';

    tip.style.left =
      '';

    tip.style.removeProperty(
      '--arrow-x'
    );

    tip.style.visibility =
      '';
  }

  function closeAllTipsEverywhere() {
    document
      .querySelectorAll(
        '[data-js-addon-tip]'
      )
      .forEach((tip) => {
        tip.classList.remove(
          'is-open',
          'is-above'
        );

        tip.setAttribute(
          'aria-hidden',
          'true'
        );

        if (
          tip.hasAttribute(
            'inert'
          )
        ) {
          tip.removeAttribute(
            'inert'
          );
        }

        resetTipInlinePosition(
          tip
        );
      });
  }

  function openTipForButton(
    btn,
    tip
  ) {
    if (
      !btn ||
      !tip
    ) {
      return;
    }

    tip.classList.add(
      'is-open'
    );

    tip.classList.remove(
      'is-above'
    );

    tip.setAttribute(
      'aria-hidden',
      'false'
    );

    if (
      tip.hasAttribute(
        'inert'
      )
    ) {
      tip.removeAttribute(
        'inert'
      );
    }

    const btnRect =
      btn.getBoundingClientRect();

    const prevVis =
      tip.style.visibility;

    tip.style.visibility =
      'hidden';

    tip.style.position =
      'fixed';

    const tipRect =
      tip.getBoundingClientRect();

    const vw =
      window.innerWidth;

    const vh =
      window.innerHeight;

    let top =
      btnRect.bottom +
      10;

    let left =
      btnRect.left +
      btnRect.width / 2 -
      tipRect.width / 2;

    left =
      Math.max(
        12,
        Math.min(
          left,
          vw -
            tipRect.width -
            12
        )
      );

    if (
      top +
        tipRect.height +
        12 >
      vh
    ) {
      top =
        btnRect.top -
        tipRect.height -
        10;

      tip.classList.add(
        'is-above'
      );
    }

    top =
      Math.max(
        8,
        top
      );

    tip.style.top =
      `${top}px`;

    tip.style.left =
      `${left}px`;

    const arrowCenter =
      btnRect.left +
      btnRect.width / 2 -
      left;

    tip.style.setProperty(
      '--arrow-x',
      `${arrowCenter}px`
    );

    tip.style.visibility =
      prevVis ||
      '';
  }

  function hardStopEvent(
    e
  ) {
    e.preventDefault();
    e.stopPropagation();

    if (
      typeof e.stopImmediatePropagation ===
      'function'
    ) {
      e.stopImmediatePropagation();
    }
  }

  document.addEventListener(
    'pointerdown',
    (e) => {
      const btn =
        e.target &&
        e.target.closest
          ? e.target.closest(
              '[data-js-addon-info-button]'
            )
          : null;

      if (
        !btn ||
        !isBestsellerInfoButton(
          btn
        )
      ) {
        return;
      }

      hardStopEvent(e);
    },
    true
  );

  document.addEventListener(
    'mousedown',
    (e) => {
      const btn =
        e.target &&
        e.target.closest
          ? e.target.closest(
              '[data-js-addon-info-button]'
            )
          : null;

      if (
        !btn ||
        !isBestsellerInfoButton(
          btn
        )
      ) {
        return;
      }

      hardStopEvent(e);
    },
    true
  );

  document.addEventListener(
    'click',
    (e) => {
      const btn =
        e.target &&
        e.target.closest
          ? e.target.closest(
              '[data-js-addon-info-button]'
            )
          : null;

      if (
        !btn ||
        !isBestsellerInfoButton(
          btn
        )
      ) {
        return;
      }

      const tip =
        getTipFromButton(
          btn
        );

      if (!tip) {
        return;
      }

      hardStopEvent(e);

      try {
        btn.blur();
      } catch (err) {}

      const wasOpen =
        tip.classList.contains(
          'is-open'
        );

      closeAllTipsEverywhere();

      if (wasOpen) {
        return;
      }

      openTipForButton(
        btn,
        tip
      );

      setTimeout(
        () =>
          openTipForButton(
            btn,
            tip
          ),
        0
      );

      setTimeout(
        () =>
          openTipForButton(
            btn,
            tip
          ),
        50
      );

      setTimeout(
        () =>
          openTipForButton(
            btn,
            tip
          ),
        150
      );
    },
    true
  );
})();